import React, { useState, useEffect } from 'react';
import { leavePoliciesApi, leaveTypeApi } from '../services/api';
import { LeavePolicy, LeavePolicyType, CreateLeavePolicyRequest, UpdateLeavePolicyRequest, CreateLeavePolicyTypeRequest, LeaveType } from '../types';
import { Plus, Edit, Trash2, Save, X, Calendar, Clock, CheckCircle, AlertCircle, FileText, Settings as SettingsIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LeavePolicies: React.FC = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState<LeavePolicy[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<LeavePolicy | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Leave types state
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState<LeaveType[]>([]);
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState<Map<number, Partial<CreateLeavePolicyTypeRequest>>>(new Map());
  const [expandedLeaveTypes, setExpandedLeaveTypes] = useState<Set<number>>(new Set());

  // Form state
  const [formData, setFormData] = useState<Partial<CreateLeavePolicyRequest>>({
    policyName: '',
    description: '',
    isDefault: false,
    leaveYearStartMonth: 1,
    leaveYearStartDay: 1,
    allowCarryForward: true,
    maxCarryForwardDays: 5,
    carryForwardExpiryMonths: 6,
    minNoticeRequiredDays: 7,
    accruesDuringNoticePeriod: true,
    payOutOnTermination: true,
    isActive: true,
  });

  useEffect(() => {
    loadPolicies();
    loadLeaveTypes();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const data = await leavePoliciesApi.getAll();
      setPolicies(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load leave policies');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaveTypes = async () => {
    try {
      const data = await leaveTypeApi.getAll();
      setAvailableLeaveTypes(data);
    } catch (err: any) {
      console.error('Failed to load leave types:', err);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setIsEditing(false);
    setSelectedPolicy(null);
    setSelectedLeaveTypes(new Map());
    setExpandedLeaveTypes(new Set());
    setFormData({
      policyName: '',
      description: '',
      isDefault: false,
      leaveYearStartMonth: 1,
      leaveYearStartDay: 1,
      allowCarryForward: true,
      maxCarryForwardDays: 5,
      carryForwardExpiryMonths: 6,
      minNoticeRequiredDays: 7,
      accruesDuringNoticePeriod: true,
      payOutOnTermination: true,
      isActive: true,
    });
  };

  const handleEdit = (policy: LeavePolicy) => {
    setIsEditing(true);
    setIsCreating(false);
    setSelectedPolicy(policy);
    
    // Load existing leave types into the form
    const leaveTypesMap = new Map<number, Partial<CreateLeavePolicyTypeRequest>>();
    if (policy.leaveTypes) {
      policy.leaveTypes.forEach(lt => {
        leaveTypesMap.set(lt.leaveTypeId, {
          leaveTypeId: lt.leaveTypeId,
          entitlementDays: lt.entitlementDays,
          accrualMethod: lt.accrualMethod,
          accrualRate: lt.accrualRate,
          proRateForPartTime: lt.proRateForPartTime,
          minServiceMonthsBeforeTaking: lt.minServiceMonthsBeforeTaking,
          maxConsecutiveDays: lt.maxConsecutiveDays,
          minConsecutiveDays: lt.minConsecutiveDays,
          requiresApproval: lt.requiresApproval,
          requiresDocumentation: lt.requiresDocumentation,
          documentationRequiredAfterDays: lt.documentationRequiredAfterDays,
          allowCarryForward: lt.allowCarryForward,
          maxCarryForwardDays: lt.maxCarryForwardDays,
          allowHalfDays: lt.allowHalfDays,
          allowHourlyBooking: lt.allowHourlyBooking,
          publicHolidaysDeductFromEntitlement: lt.publicHolidaysDeductFromEntitlement,
          maxDaysPerYear: lt.maxDaysPerYear,
          allowUnpaidAfterExhausted: lt.allowUnpaidAfterExhausted,
          customRulesJson: lt.customRulesJson,
          isActive: lt.isActive,
        });
      });
    }
    setSelectedLeaveTypes(leaveTypesMap);
    setExpandedLeaveTypes(new Set());
    
    setFormData({
      policyName: policy.policyName,
      description: policy.description,
      isDefault: policy.isDefault,
      leaveYearStartMonth: policy.leaveYearStartMonth,
      leaveYearStartDay: policy.leaveYearStartDay,
      allowCarryForward: policy.allowCarryForward,
      maxCarryForwardDays: policy.maxCarryForwardDays,
      carryForwardExpiryMonths: policy.carryForwardExpiryMonths,
      minNoticeRequiredDays: policy.minNoticeRequiredDays,
      accruesDuringNoticePeriod: policy.accruesDuringNoticePeriod,
      payOutOnTermination: policy.payOutOnTermination,
      isActive: policy.isActive,
    });
  };

  const handleViewDetails = (policy: LeavePolicy) => {
    setSelectedPolicy(policy);
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        // Create policy with leave types
        const leaveTypes = Array.from(selectedLeaveTypes.values());
        const policyData = { ...formData, leaveTypes };
        await leavePoliciesApi.create(policyData as CreateLeavePolicyRequest);
        showSuccess('Leave policy created successfully');
      } else if (isEditing && selectedPolicy) {
        // Update policy basic info
        await leavePoliciesApi.update(selectedPolicy.leavePolicyId, formData as UpdateLeavePolicyRequest);
        
        // Update leave types
        const existingLeaveTypeIds = new Set(selectedPolicy.leaveTypes?.map(lt => lt.leaveTypeId) || []);
        const newLeaveTypeIds = new Set(selectedLeaveTypes.keys());
        
        // Add new leave types
        const leaveTypeEntries = Array.from(selectedLeaveTypes.entries());
        for (const [leaveTypeId, config] of leaveTypeEntries) {
          if (!existingLeaveTypeIds.has(leaveTypeId)) {
            await leavePoliciesApi.addLeaveType(selectedPolicy.leavePolicyId, config as CreateLeavePolicyTypeRequest);
          } else {
            // Update existing leave type
            await leavePoliciesApi.updateLeaveType(selectedPolicy.leavePolicyId, leaveTypeId, config);
          }
        }
        
        // Remove deleted leave types
        const existingLeaveTypeIdsArray = Array.from(existingLeaveTypeIds);
        for (const leaveTypeId of existingLeaveTypeIdsArray) {
          if (!newLeaveTypeIds.has(leaveTypeId)) {
            await leavePoliciesApi.removeLeaveType(selectedPolicy.leavePolicyId, leaveTypeId);
          }
        }
        
        showSuccess('Leave policy updated successfully');
      }
      setIsCreating(false);
      setIsEditing(false);
      setSelectedPolicy(null);
      setSelectedLeaveTypes(new Map());
      await loadPolicies();
    } catch (err: any) {
      setError(err.message || 'Failed to save leave policy');
    }
  };

  const handleAddLeaveType = (leaveTypeId: number) => {
    const leaveType = availableLeaveTypes.find(lt => lt.leaveTypeId === leaveTypeId);
    if (!leaveType) return;
    
    // Set defaults based on Irish employment law
    let defaults: Partial<CreateLeavePolicyTypeRequest> = {
      leaveTypeId,
      entitlementDays: 20,
      accrualMethod: 'Monthly',
      proRateForPartTime: true,
      requiresApproval: true,
      requiresDocumentation: false,
      allowCarryForward: false,
      allowHalfDays: true,
      allowHourlyBooking: false,
      publicHolidaysDeductFromEntitlement: false,
      allowUnpaidAfterExhausted: false,
      isActive: true,
    };
    
    // Apply Irish law defaults based on leave type name
    switch (leaveType.name.toLowerCase()) {
      case 'annual leave':
        defaults = {
          ...defaults,
          entitlementDays: 20, // Irish statutory minimum: 4 weeks (20 days)
          accrualMethod: 'Monthly',
          accrualRate: 0.08333333, // 1.667 days per month
          proRateForPartTime: true,
          minServiceMonthsBeforeTaking: 1, // Must work 1 month before taking leave
          requiresApproval: true,
          requiresDocumentation: false,
          allowCarryForward: true,
          maxCarryForwardDays: 5, // Up to 5 days, must use within 6 months
          allowHalfDays: true,
        };
        break;
        
      case 'sick leave':
        defaults = {
          ...defaults,
          entitlementDays: 10, // Year 4+ (3 days year 1, 5 days year 2, 7 days year 3, 10 days year 4+)
          accrualMethod: 'Monthly',
          accrualRate: 0.83333333, // Accrues monthly
          proRateForPartTime: false, // Not pro-rated
          minServiceMonthsBeforeTaking: 6, // 6 months service required
          requiresApproval: false, // Usually notified, not pre-approved
          requiresDocumentation: true,
          documentationRequiredAfterDays: 2, // Medical cert required after 2 consecutive days
          allowCarryForward: false,
          allowHalfDays: true,
        };
        break;
        
      case 'maternity leave':
        defaults = {
          ...defaults,
          entitlementDays: 182, // 26 weeks (182 days) paid, 16 weeks unpaid = 42 weeks total
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: true,
          requiresDocumentation: true,
          allowCarryForward: false,
          allowHalfDays: false, // Typically taken as full weeks
        };
        break;
        
      case 'paternity leave':
        defaults = {
          ...defaults,
          entitlementDays: 14, // 2 weeks (14 days)
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: true,
          requiresDocumentation: true,
          allowCarryForward: false,
          allowHalfDays: false,
        };
        break;
        
      case "parent's leave":
        defaults = {
          ...defaults,
          entitlementDays: 35, // 7 weeks (35 days), must be taken before child is 2
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: true,
          requiresDocumentation: true,
          allowCarryForward: false,
          allowHalfDays: false,
        };
        break;
        
      case 'adoptive leave':
        defaults = {
          ...defaults,
          entitlementDays: 168, // 24 weeks paid, 16 weeks unpaid
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: true,
          requiresDocumentation: true,
          allowCarryForward: false,
          allowHalfDays: false,
        };
        break;
        
      case "carer's leave":
        defaults = {
          ...defaults,
          entitlementDays: 0, // Unpaid, up to 104 weeks over 2 years
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 12, // 12 months continuous service
          requiresApproval: true,
          requiresDocumentation: true,
          allowCarryForward: false,
          allowHalfDays: false,
        };
        break;
        
      case 'force majeure leave':
        defaults = {
          ...defaults,
          entitlementDays: 3, // 3 days per year, 5 days per 12 months
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: false, // Emergency leave, notification not pre-approval
          requiresDocumentation: false,
          allowCarryForward: false,
          allowHalfDays: true,
          maxDaysPerYear: 3,
        };
        break;
        
      case 'bereavement leave':
        defaults = {
          ...defaults,
          entitlementDays: 3, // 3 days paid per bereavement event
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: false, // Notification, not pre-approval
          requiresDocumentation: false,
          allowCarryForward: false,
          allowHalfDays: true,
        };
        break;
        
      case 'public holiday':
        defaults = {
          ...defaults,
          entitlementDays: 9, // 9 public holidays per year in Ireland
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: false,
          requiresDocumentation: false,
          allowCarryForward: false,
          allowHalfDays: false,
          publicHolidaysDeductFromEntitlement: false,
        };
        break;
        
      case 'unpaid leave':
        defaults = {
          ...defaults,
          entitlementDays: 0, // Discretionary
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: true,
          requiresDocumentation: false,
          allowCarryForward: false,
          allowHalfDays: true,
        };
        break;
        
      case 'study leave':
        defaults = {
          ...defaults,
          entitlementDays: 0, // Typically unpaid, discretionary
          accrualMethod: 'Upfront',
          proRateForPartTime: false,
          minServiceMonthsBeforeTaking: 0,
          requiresApproval: true,
          requiresDocumentation: true, // Usually requires proof of enrollment
          allowCarryForward: false,
          allowHalfDays: true,
        };
        break;
    }
    
    const newMap = new Map(selectedLeaveTypes);
    newMap.set(leaveTypeId, defaults);
    setSelectedLeaveTypes(newMap);
    
    // Auto-expand the newly added leave type
    const newExpanded = new Set(expandedLeaveTypes);
    newExpanded.add(leaveTypeId);
    setExpandedLeaveTypes(newExpanded);
  };

  const handleRemoveLeaveType = (leaveTypeId: number) => {
    const newMap = new Map(selectedLeaveTypes);
    newMap.delete(leaveTypeId);
    setSelectedLeaveTypes(newMap);
    
    const newExpanded = new Set(expandedLeaveTypes);
    newExpanded.delete(leaveTypeId);
    setExpandedLeaveTypes(newExpanded);
  };

  const handleUpdateLeaveType = (leaveTypeId: number, updates: Partial<CreateLeavePolicyTypeRequest>) => {
    const newMap = new Map(selectedLeaveTypes);
    const existing = newMap.get(leaveTypeId) || {};
    newMap.set(leaveTypeId, { ...existing, ...updates });
    setSelectedLeaveTypes(newMap);
  };

  const toggleLeaveTypeExpanded = (leaveTypeId: number) => {
    const newExpanded = new Set(expandedLeaveTypes);
    if (newExpanded.has(leaveTypeId)) {
      newExpanded.delete(leaveTypeId);
    } else {
      newExpanded.add(leaveTypeId);
    }
    setExpandedLeaveTypes(newExpanded);
  };

  const handleDelete = async (policyId: number) => {
    if (!window.confirm('Are you sure you want to delete this leave policy?')) return;
    
    try {
      await leavePoliciesApi.delete(policyId);
      showSuccess('Leave policy deleted successfully');
      await loadPolicies();
      if (selectedPolicy?.leavePolicyId === policyId) {
        setSelectedPolicy(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete leave policy');
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    setSelectedPolicy(null);
  };

  const getMonthName = (month: number) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Leave Policies</h1>
            <p className="text-gray-600 mt-1">Manage company-wide leave policies based on Irish employment law</p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            New Policy
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle size={20} />
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Sidebar - Policy List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Policies</h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {policies.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No policies found. Create your first policy.
                </div>
              ) : (
                policies.map((policy) => (
                  <div
                    key={policy.leavePolicyId}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                      selectedPolicy?.leavePolicyId === policy.leavePolicyId ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                    onClick={() => handleViewDetails(policy)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{policy.policyName}</h3>
                          {policy.isDefault && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Default</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{policy.description || 'No description'}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Calendar size={14} />
                          {getMonthName(policy.leaveYearStartMonth)} {policy.leaveYearStartDay}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(policy);
                          }}
                          className="p-1 hover:bg-blue-100 rounded text-blue-600"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(policy.leavePolicyId);
                          }}
                          className="p-1 hover:bg-red-100 rounded text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Details/Form */}
        <div className="lg:col-span-2">
          {(isCreating || isEditing) ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {isCreating ? 'Create New Policy' : 'Edit Policy'}
                </h2>
                <button onClick={handleCancel} className="p-2 hover:bg-gray-100 rounded">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Policy Name *
                      </label>
                      <input
                        type="text"
                        value={formData.policyName}
                        onChange={(e) => setFormData({ ...formData, policyName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., Standard Irish Policy"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                        placeholder="Describe this policy..."
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isDefault"
                        checked={formData.isDefault}
                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                        Set as default policy for new employees
                      </label>
                    </div>
                  </div>
                </div>

                {/* Leave Year Configuration */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Leave Year Configuration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Leave Year Start Month
                      </label>
                      <select
                        value={formData.leaveYearStartMonth}
                        onChange={(e) => setFormData({ ...formData, leaveYearStartMonth: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                          <option key={month} value={month}>{getMonthName(month)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Leave Year Start Day
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.leaveYearStartDay}
                        onChange={(e) => setFormData({ ...formData, leaveYearStartDay: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Carry Forward Rules */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Carry Forward Rules</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="allowCarryForward"
                        checked={formData.allowCarryForward}
                        onChange={(e) => setFormData({ ...formData, allowCarryForward: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="allowCarryForward" className="ml-2 text-sm text-gray-700">
                        Allow carry forward of unused leave
                      </label>
                    </div>
                    {formData.allowCarryForward && (
                      <div className="grid grid-cols-2 gap-4 ml-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Max Days to Carry Forward
                          </label>
                          <input
                            type="number"
                            step="0.5"
                            value={formData.maxCarryForwardDays || ''}
                            onChange={(e) => setFormData({ ...formData, maxCarryForwardDays: parseFloat(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., 5"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiry Period (months)
                          </label>
                          <input
                            type="number"
                            value={formData.carryForwardExpiryMonths || ''}
                            onChange={(e) => setFormData({ ...formData, carryForwardExpiryMonths: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., 6 (Irish law)"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Other Rules */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Other Rules</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum Notice Required (days)
                      </label>
                      <input
                        type="number"
                        value={formData.minNoticeRequiredDays}
                        onChange={(e) => setFormData({ ...formData, minNoticeRequiredDays: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="accruesDuringNoticePeriod"
                        checked={formData.accruesDuringNoticePeriod}
                        onChange={(e) => setFormData({ ...formData, accruesDuringNoticePeriod: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="accruesDuringNoticePeriod" className="ml-2 text-sm text-gray-700">
                        Leave accrues during notice period
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="payOutOnTermination"
                        checked={formData.payOutOnTermination}
                        onChange={(e) => setFormData({ ...formData, payOutOnTermination: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="payOutOnTermination" className="ml-2 text-sm text-gray-700">
                        Pay out unused leave on termination
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                {/* Leave Types Configuration */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Leave Types</h3>
                  
                  {/* Add Leave Type Dropdown */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Leave Type to Policy
                    </label>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddLeaveType(parseInt(e.target.value));
                          e.target.value = '';
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">-- Select a leave type to add --</option>
                      {availableLeaveTypes
                        .filter(lt => !selectedLeaveTypes.has(lt.leaveTypeId))
                        .map(lt => (
                          <option key={lt.leaveTypeId} value={lt.leaveTypeId}>
                            {lt.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Selected Leave Types List */}
                  <div className="space-y-3">
                    {Array.from(selectedLeaveTypes.entries()).map(([leaveTypeId, config]) => {
                      const leaveType = availableLeaveTypes.find(lt => lt.leaveTypeId === leaveTypeId);
                      if (!leaveType) return null;
                      
                      const isExpanded = expandedLeaveTypes.has(leaveTypeId);
                      
                      return (
                        <div key={leaveTypeId} className="border border-gray-300 rounded-lg overflow-hidden">
                          {/* Leave Type Header */}
                          <div className="bg-gray-50 p-3 flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => toggleLeaveTypeExpanded(leaveTypeId)}
                              className="flex items-center gap-2 flex-1 text-left hover:text-blue-600"
                            >
                              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              <span className="font-semibold">{leaveType.name}</span>
                              <span className="text-sm text-gray-600">
                                ({config.entitlementDays || 0} days)
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveLeaveType(leaveTypeId)}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          
                          {/* Leave Type Configuration (Expandable) */}
                          {isExpanded && (
                            <div className="p-4 space-y-4 bg-white">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Entitlement Days/Year *
                                  </label>
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={config.entitlementDays || ''}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { entitlementDays: parseFloat(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Accrual Method
                                  </label>
                                  <select
                                    value={config.accrualMethod || 'Monthly'}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { accrualMethod: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="Monthly">Monthly</option>
                                    <option value="Continuous">Continuous</option>
                                    <option value="Upfront">Upfront</option>
                                    <option value="PerHourWorked">Per Hour Worked</option>
                                  </select>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Min Service Months Before Taking
                                  </label>
                                  <input
                                    type="number"
                                    value={config.minServiceMonthsBeforeTaking || ''}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { minServiceMonthsBeforeTaking: parseInt(e.target.value) || undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Optional"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Documentation After (days)
                                  </label>
                                  <input
                                    type="number"
                                    value={config.documentationRequiredAfterDays || ''}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { documentationRequiredAfterDays: parseInt(e.target.value) || undefined })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Optional"
                                  />
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={config.requiresApproval ?? true}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { requiresApproval: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">Requires Approval</label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={config.requiresDocumentation ?? false}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { requiresDocumentation: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">Requires Documentation</label>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={config.allowHalfDays ?? true}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { allowHalfDays: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">Allow Half Days</label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={config.allowCarryForward ?? false}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { allowCarryForward: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">Allow Carry Forward</label>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={config.proRateForPartTime ?? true}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { proRateForPartTime: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">Pro-rate for Part-time</label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={config.isActive ?? true}
                                    onChange={(e) => handleUpdateLeaveType(leaveTypeId, { isActive: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 text-sm text-gray-700">Active</label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {selectedLeaveTypes.size === 0 && (
                      <div className="text-center text-gray-500 py-4 border border-dashed border-gray-300 rounded-lg">
                        No leave types added yet. Select a leave type from the dropdown above.
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                  >
                    <Save size={18} />
                    Save Policy
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition"
                  >
                    <X size={18} />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : selectedPolicy ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedPolicy.policyName}</h2>
                  {selectedPolicy.isDefault && (
                    <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Default Policy</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(selectedPolicy)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                  >
                    <Edit size={18} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(selectedPolicy.leavePolicyId)}
                    className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    <Trash2 size={18} />
                    Delete
                  </button>
                </div>
              </div>

              {selectedPolicy.description && (
                <p className="text-gray-600 mb-6">{selectedPolicy.description}</p>
              )}

              <div className="space-y-6">
                {/* Policy Configuration */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <SettingsIcon size={20} />
                    Policy Configuration
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Leave Year Starts:</span>
                      <span className="font-medium">{getMonthName(selectedPolicy.leaveYearStartMonth)} {selectedPolicy.leaveYearStartDay}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Minimum Notice Required:</span>
                      <span className="font-medium">{selectedPolicy.minNoticeRequiredDays} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Carry Forward Allowed:</span>
                      <span className="font-medium">{selectedPolicy.allowCarryForward ? 'Yes' : 'No'}</span>
                    </div>
                    {selectedPolicy.allowCarryForward && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600 ml-4">Max Carry Forward:</span>
                          <span className="font-medium">{selectedPolicy.maxCarryForwardDays || 'Unlimited'} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 ml-4">Expiry Period:</span>
                          <span className="font-medium">{selectedPolicy.carryForwardExpiryMonths || 'No expiry'} months</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Accrues During Notice:</span>
                      <span className="font-medium">{selectedPolicy.accruesDuringNoticePeriod ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pay Out on Termination:</span>
                      <span className="font-medium">{selectedPolicy.payOutOnTermination ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>

                {/* Leave Types */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText size={20} />
                    Leave Types in Policy ({selectedPolicy.leaveTypes?.length || 0})
                  </h3>
                  {selectedPolicy.leaveTypes && selectedPolicy.leaveTypes.length > 0 ? (
                    <div className="space-y-3">
                      {selectedPolicy.leaveTypes.map((leaveType) => (
                        <div key={leaveType.leavePolicyTypeId} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-gray-900">{leaveType.leaveTypeName}</h4>
                            <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {leaveType.entitlementDays} days/year
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <div>Accrual: {leaveType.accrualMethod}</div>
                            <div>Approval: {leaveType.requiresApproval ? 'Required' : 'Not required'}</div>
                            <div>Documentation: {leaveType.requiresDocumentation ? 'Required' : 'Not required'}</div>
                            <div>Half Days: {leaveType.allowHalfDays ? 'Allowed' : 'Not allowed'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                      No leave types configured yet. Edit this policy to add leave types.
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
                  <div>Created: {new Date(selectedPolicy.createdAt).toLocaleString()}</div>
                  {selectedPolicy.updatedAt && (
                    <div>Updated: {new Date(selectedPolicy.updatedAt).toLocaleString()}</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Policy Selected</h3>
              <p className="text-gray-600 mb-4">Select a policy from the list or create a new one to get started.</p>
              <button
                onClick={handleCreate}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                <Plus size={20} />
                Create New Policy
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeavePolicies;
